import { useMemo } from "react";
import { useAggrMetricsAcrossDimensions } from "../../WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { annoDimForRequest } from "../../utils/annoDimForRequest";


/**
 * The per-value counts behind a categorical metric card: one row per value of
 * the chosen field, which the card then aggregates over the selected values.
 *
 * Two field families, one request shape:
 *   document field  the bare `valuePath` string, as before.
 *   annotation      Topics has NO valuePath — membership lives on the
 *                   clustering tree — so it travels as the dimension object
 *                   annoDimForRequest builds ({ dimensionType, level,
 *                   parentTopicNodeId? }), against contentType "annotations",
 *                   the only collection the BE accepts it on. Rows are then
 *                   topics (or one topic's sub-topics on a drill), counted in
 *                   annotations.
 */
export const useGetFieldValueCounts = ({
  contentType,
  field,
  activeFilters = [],
  queryOptions = {},
  vizCardId
}) => {
  const selectedField = field;
  
  const isNumberInput = selectedField?.comp === 'NumberInput';
  // Annotation-based fields are stamped `isAnno` by their option builder;
  // Topics carries a `dimensionType` in place of the valuePath every document
  // field groups by, hence the two-way shouldFetch gate below.
  const isAnnoField = !!selectedField?.isAnno;
  const shouldFetch =
    !isNumberInput &&
    contentType &&
    (selectedField?.valuePath || selectedField?.dimensionType);

  const { data, status, error } = useAggrMetricsAcrossDimensions({
    vizCardId: vizCardId ? `${vizCardId}_fieldValueCounts` : undefined,
    contentType,
    dimensions: shouldFetch
      ? [isAnnoField ? annoDimForRequest(selectedField) : selectedField.valuePath]
      : [],
    metrics: [{ type: "count", name: "count" }],
    activeFilters,
    queryOptions: {
      enabled: shouldFetch && (queryOptions.enabled !== false),
      ...queryOptions
    }
  });

  const fieldValueCounts = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    return data.map(item => ({
      value: item.value,
      display: item.display || item.value,
      count: item.count || 0,
      percentage: item.percentage
    }));
  }, [data]);

  return {
    data: fieldValueCounts,
    status,
    error
  };
};

